import React, { useEffect, useState, useRef } from 'react'
import { Table, Button, Modal, Form, Spinner, Badge, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import { getTemplates, createTemplate, editTemplate, archiveTemplate, importTemplates, resetTemplateResult } from '../../actions/productTemplateActions'
import { getDepartmentsForSelect } from '../../actions/departmentsActions'
import ReactPaginate from 'react-paginate'
import * as XLSX from 'xlsx'

const emptyTemplate = { name: '', department: '', attributes: [] }
const emptyAttr = { key: '', label: '' }

export default function ProductTemplates() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { loading, resulttemplate, templates, total } = useSelector(s => s.productTemplates)
    const { departments } = useSelector(s => s.departments)

    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const limit = 20

    const [showModal, setShowModal] = useState(false)
    const [form, setForm] = useState({ ...emptyTemplate })
    const [editingId, setEditingId] = useState(null)

    const [showImport, setShowImport] = useState(false)
    const [importRows, setImportRows] = useState([])
    const fileRef = useRef()

    useEffect(() => { dispatch(getTemplates(page, limit, search)) }, [dispatch, page, search])
    useEffect(() => { dispatch(getDepartmentsForSelect()) }, [dispatch])

    useEffect(() => {
        if (!resulttemplate) return
        if (resulttemplate?.startsWith('Error') || resulttemplate?.startsWith('Token')) {
            Swal.fire({ icon: 'error', title: resulttemplate })
        } else {
            const transKey = 'admin.templates.' + resulttemplate.replace(/^templates?/, '').charAt(0).toLowerCase() + resulttemplate.replace(/^templates?/, '').slice(1)
            const title = t(transKey) !== transKey ? t(transKey) : resulttemplate
            Swal.fire({ icon: 'success', title })
        }
        dispatch(resetTemplateResult())
    }, [resulttemplate, dispatch, t])

    const openCreate = () => {
        setEditingId(null)
        setForm({ ...emptyTemplate, attributes: [{ ...emptyAttr }] })
        setShowModal(true)
    }

    const openEdit = (tpl) => {
        setEditingId(tpl._id)
        setForm({
            name: tpl.name,
            department: tpl.department || '',
            attributes: tpl.attributes.map(a => ({ key: a.key, label: a.label })),
        })
        setShowModal(true)
    }

    const handleSave = () => {
        if (!form.name.trim()) return Swal.fire({ icon: 'warning', title: 'El nombre es obligatorio' })
        const payload = {
            ...form,
            attributes: form.attributes.map(({ key, label }) => ({ key, label })),
        }
        if (editingId) {
            dispatch(editTemplate(editingId, payload))
        } else {
            dispatch(createTemplate(payload))
        }
        setShowModal(false)
    }

    const handleArchive = (id) => {
        Swal.fire({
            icon: 'question',
            title: '¿Archivar plantilla?',
            showCancelButton: true,
            confirmButtonText: 'Sí',
        }).then(r => { if (r.isConfirmed) dispatch(archiveTemplate(id)) })
    }

    const labelToKey = (label) => {
        return label
            .toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_|_$/g, '')
            || 'attr'
    }

    const addAttr = () => setForm(f => ({ ...f, attributes: [...f.attributes, { ...emptyAttr }] }))
    const removeAttr = (i) => {
        Swal.fire({
            icon: 'question',
            title: '¿Eliminar atributo?',
            text: 'El atributo se eliminará de todos los productos, incluidos los que ya tienen valor. ¿Está seguro?',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(r => {
            if (r.isConfirmed) setForm(f => ({ ...f, attributes: f.attributes.filter((_, idx) => idx !== i) }))
        })
    }
    const setLabel = (i, value) => {
        setForm(f => ({
            ...f,
            attributes: f.attributes.map((a, idx) => idx === i ? { ...a, label: value, key: labelToKey(value) } : a)
        }))
    }

    const [sheetName, setSheetName] = useState('')

    const handleFile = (e) => {
        const file = e.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const wb = XLSX.read(ev.target.result, { type: 'array' })
            setSheetName(wb.SheetNames[0] || '')
            const ws = wb.Sheets[wb.SheetNames[0]]
            const json = XLSX.utils.sheet_to_json(ws)
            setImportRows(json)
        }
        reader.readAsArrayBuffer(file)
    }

    const handleImport = async () => {
        if (!importRows.length) return
        try {
            const r = await dispatch(importTemplates({ rows: importRows, sheetName }))
            if (r) Swal.fire({ icon: 'success', title: r.message || 'Importadas' })
            dispatch(getTemplates(page, limit, search))
        } catch {
            Swal.fire({ icon: 'error', title: 'Error al importar' })
        }
        setShowImport(false)
        setImportRows([])
        setSheetName('')
        if (fileRef.current) fileRef.current.value = ''
    }

    return (
        <>
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="input-group" style={{ maxWidth: '320px' }}>
                        <span className="input-group-text"><i className="fas fa-search"></i></span>
                        <Form.Control
                            type="text"
                            placeholder={t('admin.templates.search', 'Buscar plantilla...')}
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1) }}
                        />
                    </div>
                </div>
                <div className="toolbar-right">
                    <Button variant="primary" onClick={() => setShowImport(true)}>
                        <i className="fas fa-file-import" />{t('admin.templates.import', 'Importar')}
                    </Button>
                    <Button variant="success" onClick={openCreate}>
                        <i className="fas fa-plus" />{t('admin.templates.create', 'Crear')}
                    </Button>
                </div>
            </div>

            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            {!loading && (
                <div className="card-simple p-0">
                    <Table hover responsive>
                        <thead>
                            <tr>
                                <th>{t('admin.templates.name', 'Nombre')}</th>
                                <th>{t('admin.templates.attributesCount', 'Atributos')}</th>
                                <th>{t('admin.templates.department', 'Departamento')}</th>
                                <th style={{ width: 160 }}>{t('global.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map(tpl => (
                                <tr key={tpl._id}>
                                    <td className="fw-500">{tpl.name}</td>
                                    <td>
                                        <Badge bg="secondary">{tpl.attributes?.length || 0}</Badge>
                                    </td>
                                    <td>
                                        {tpl.department ? (
                                            <Badge bg="primary">{tpl.department}</Badge>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="outline-primary" onClick={() => openEdit(tpl)}>
                                                <i className="fas fa-edit" />
                                            </Button>
                                            {tpl.active && (
                                                <Button size="sm" variant="outline-danger" onClick={() => handleArchive(tpl._id)}>
                                                    <i className="fas fa-archive" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!templates.length && (
                                <tr><td colSpan={4} className="text-center py-4" style={{ color: 'var(--neutral-400)' }}>{t('admin.templates.noData', 'Sin resultados')}</td></tr>
                            )}
                        </tbody>
                    </Table>

                    {total > limit && (
                        <div className="p-3 border-top">
                            <ReactPaginate
                                previousLabel="<"
                                nextLabel=">"
                                breakLabel="..."
                                pageCount={Math.ceil(total / limit)}
                                marginPagesDisplayed={2}
                                pageRangeDisplayed={3}
                                onPageChange={d => setPage(d.selected + 1)}
                                containerClassName="pagination justify-content-center mb-0"
                                activeClassName="active"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Modal crear/editar */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingId ? t('admin.templates.editTitle', 'Editar Plantilla') : t('admin.templates.createTitle', 'Nueva Plantilla')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('admin.templates.name', 'Nombre')}</Form.Label>
                        <Form.Control value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('admin.templates.department', 'Departamento')}</Form.Label>
                        <Form.Select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
                            <option value="">{t('admin.templates.selectDepartment', 'Seleccionar...')}</option>
                            {departments.map(d => (
                                <option key={d._id} value={d.code}>{d.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <hr />
                    <div className="d-flex justify-content-between mb-2">
                        <strong>{t('admin.templates.attributes', 'Atributos')}</strong>
                        <Button size="sm" variant="outline-success" onClick={addAttr}>
                            <i className="fas fa-plus" />
                        </Button>
                    </div>
                    {form.attributes.map((attr, i) => (
                        <Row key={i} className="mb-2 g-2 align-items-center">
                            <Col>
                                <Form.Control
                                    size="sm"
                                    placeholder={t('admin.templates.attrLabel', 'Etiqueta')}
                                    value={attr.label}
                                    onChange={e => setLabel(i, e.target.value)}
                                />
                            </Col>
                            <Col xs={1}>
                                <Button size="sm" variant="outline-danger" onClick={() => removeAttr(i)}>
                                    <i className="fas fa-times" />
                                </Button>
                            </Col>
                        </Row>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>{t('global.cancel')}</Button>
                    <Button variant="primary" onClick={handleSave}>{t('global.save')}</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal importar */}
            <Modal show={showImport} onHide={() => { setShowImport(false); setImportRows([]) }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('admin.templates.importTitle', 'Importar Plantillas')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('admin.templates.importFile', 'Archivo Excel')}</Form.Label>
                        <Form.Control type="file" accept=".xlsx,.xls" ref={fileRef} onChange={handleFile} />
                    </Form.Group>
                    {importRows.length > 0 && (
                        <p>{t('admin.templates.importRows', 'Filas leídas')}: {importRows.length}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowImport(false); setImportRows([]) }}>
                        {t('global.cancel')}
                    </Button>
                    <Button variant="primary" onClick={handleImport} disabled={!importRows.length}>
                        {t('admin.templates.importAction', 'Importar')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
